import { useEffect, useState } from 'react';
import { Checkbox, FormControlLabel, TextField } from '@mui/material';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { translatePblLawyerProfileText } from '@/api/pbl.api';
import { InputWrapper, MultiInputWrapper } from '@/components/Container/AuthContainer/styles';
import { Button } from '@/components/UI/Button';
import { Text } from '@/components/UI/Typography/Text';
import { Flex } from '@/layout';

import { buildVolunteerFullNameEn, buildVolunteerFullNameZh } from '../formState';
import { FormPanel, FormSectionTitle, SectionTitle, ValidationText } from '../styles';
import type { UpdateVolunteerForm, VolunteerFormState } from '../types';

interface Props {
  form: VolunteerFormState;
  errors: FieldErrors<VolunteerFormState>;
  control: Control<VolunteerFormState>;
  isMobile: boolean;
  isTablet: boolean;
  updateForm: UpdateVolunteerForm;
}

const ProfileCvStep = ({ form, errors, control, isMobile, isTablet, updateForm }: Props) => {
  //   @ts-ignore
  const { t } = useTranslation();
  const [isTranslatingEnToZh, setIsTranslatingEnToZh] = useState(false);
  const [isTranslatingZhToEn, setIsTranslatingZhToEn] = useState(false);
  const [translationErrorEnToZh, setTranslationErrorEnToZh] = useState('');
  const [translationErrorZhToEn, setTranslationErrorZhToEn] = useState('');
  const titleWithNumber = t('pbl.lawyerRegistration.profileCv.titleWithNumber', {
    defaultValue: '11. Profile & CV',
  });

  const translateProfessionalExperienceEnToZh = async () => {
    const text = form.cv_professional_experience_en?.trim();

    if (!text) {
      setTranslationErrorEnToZh(
        t('pbl.lawyerRegistration.profileCv.errors.enterEnglishBeforeTranslating', {
          defaultValue: 'Please enter English professional experience before translating.',
        })
      );
      return;
    }

    setIsTranslatingEnToZh(true);
    setTranslationErrorEnToZh('');

    try {
      const response = await translatePblLawyerProfileText({
        text,
        from: 'en',
        to: 'zh-Hant',
      });

      updateForm('cv_professional_experience_zh', response.data.data.translated_text);
    } catch (error) {
      setTranslationErrorEnToZh(
        t('pbl.lawyerRegistration.profileCv.errors.translateEnToZhFailed', {
          defaultValue: 'Translation failed. You can still enter the Chinese summary manually.',
        })
      );
    } finally {
      setIsTranslatingEnToZh(false);
    }
  };

  const translateProfessionalExperienceZhToEn = async () => {
    const text = form.cv_professional_experience_zh?.trim();

    if (!text) {
      setTranslationErrorZhToEn(
        t('pbl.lawyerRegistration.profileCv.errors.enterChineseBeforeTranslating', {
          defaultValue: 'Please enter Chinese professional experience before translating.',
        })
      );
      return;
    }

    setIsTranslatingZhToEn(true);
    setTranslationErrorZhToEn('');

    try {
      const response = await translatePblLawyerProfileText({
        text,
        from: 'zh-Hant',
        to: 'en',
      });

      updateForm('cv_professional_experience_en', response.data.data.translated_text);
    } catch (error) {
      setTranslationErrorZhToEn(
        t('pbl.lawyerRegistration.profileCv.errors.translateZhToEnFailed', {
          defaultValue: 'Translation failed. You can still enter the English summary manually.',
        })
      );
    } finally {
      setIsTranslatingZhToEn(false);
    }
  };

  useEffect(() => {
    const computedValues: Partial<VolunteerFormState> = {
      cv_full_name_en: buildVolunteerFullNameEn(form),
      cv_full_name_zh: buildVolunteerFullNameZh(form),
      cv_law_firm_en: form.law_firm_name,
      cv_law_firm_zh: form.law_firm_name_zh,
      cv_admission_year_en: form.hk_solicitor_admission_year,
      cv_admission_year_zh: form.hk_solicitor_admission_year,
    };

    (Object.keys(computedValues) as Array<keyof VolunteerFormState>).forEach((key) => {
      const value = computedValues[key];
      if (typeof value === 'string' && form[key] !== value) {
        updateForm(key, value as VolunteerFormState[typeof key]);
      }
    });
  }, [
    form.full_name_en,
    form.full_name_zh,
    form.given_name_en,
    form.given_name_zh,
    form.hk_solicitor_admission_year,
    form.law_firm_name,
    form.law_firm_name_zh,
    form.surname_en,
    form.surname_zh,
    updateForm,
  ]);

  return (
    <FormPanel isTablet={isTablet}>
      <SectionTitle>{t('pbl.lawyerRegistration.profileCv.section', { defaultValue: 'Section 4' })}</SectionTitle>
      <Flex gridGap={'5px'}>
        11. <FormSectionTitle>{titleWithNumber.replace(/^11\.\s*/, '')}</FormSectionTitle>
      </Flex>
      <InputWrapper marginBottom={30}>
        <Text>
          {t('pbl.lawyerRegistration.profileCv.publicationIntro', {
            defaultValue:
              "CVs of volunteer lawyers participating in FreelegalLink will be uploaded onto the FreelegalLink public webpage for the Scheme's pro bono service users' information. Please confirm your consent for publication of your CV on the FreelegalLink webpage for the above purposes.",
          })}
        </Text>
      </InputWrapper>
      <InputWrapper marginBottom={24}>
        <Controller
          control={control}
          name="cv_publication_consent"
          rules={{
            validate: (value) =>
              value ||
              t('pbl.lawyerRegistration.profileCv.errors.consentRequired', {
                defaultValue: 'Please consent before proceeding to the next step.',
              }),
          }}
          render={({ field }) => (
            <FormControlLabel
              control={<Checkbox checked={!!field.value} onChange={(event) => field.onChange(event.target.checked)} />}
              label={t('pbl.lawyerRegistration.profileCv.publicationConsent', {
                defaultValue:
                  "I consent to the publication of my CV on the LegalLink webpage hosted on eBRAM's Deal Making Portal.",
              })}
            />
          )}
        />
        {errors.cv_publication_consent?.message && (
          <ValidationText>{errors.cv_publication_consent.message}</ValidationText>
        )}
      </InputWrapper>

      <MultiInputWrapper isMobile={isMobile} marginBottom={26}>
        <InputWrapper>
          <Controller
            control={control}
            name="cv_full_name_en"
            rules={{
              required: t('pbl.lawyerRegistration.profileCv.errors.fullNameRequired', {
                defaultValue: '[Surname, Given Name] is required.',
              }),
            }}
            render={({ field }) => (
              <TextField
                label={t('pbl.lawyerRegistration.profileCv.fields.surnameGivenNameEn', {
                  defaultValue: '[Surname, Given Name] *',
                })}
                variant="standard"
                value={field.value || ''}
                onChange={field.onChange}
                fullWidth
                disabled
              />
            )}
          />
        </InputWrapper>
        <InputWrapper>
          <Controller
            control={control}
            name="cv_full_name_zh"
            rules={{
              required: t('pbl.lawyerRegistration.profileCv.errors.fullNameRequired', {
                defaultValue: '[Surname, Given Name] is required.',
              }),
            }}
            render={({ field }) => (
              <TextField
                label={t('pbl.lawyerRegistration.profileCv.fields.surnameGivenNameZh', {
                  defaultValue: '[Surname, Given Name] (Chinese) *',
                })}
                variant="standard"
                value={field.value || ''}
                onChange={field.onChange}
                fullWidth
                disabled
              />
            )}
          />
        </InputWrapper>
      </MultiInputWrapper>

      <MultiInputWrapper isMobile={isMobile} marginBottom={26}>
        <InputWrapper marginBottom={26}>
          <Controller
            control={control}
            name="cv_law_firm_en"
            rules={{
              required: t('pbl.lawyerRegistration.profileCv.errors.positionInFirmRequired', {
                defaultValue: 'Position in Firm is required.',
              }),
            }}
            render={({ field }) => (
              <TextField
                label={t('pbl.lawyerRegistration.profileCv.fields.positionInFirm', {
                  defaultValue: 'Position in Firm',
                })}
                variant="standard"
                value={field.value || ''}
                onChange={field.onChange}
                fullWidth
                disabled
              />
            )}
          />
        </InputWrapper>
        <InputWrapper marginBottom={26}>
          <Controller
            control={control}
            name="cv_law_firm_zh"
            rules={{
              required: t('pbl.lawyerRegistration.profileCv.errors.affiliatedLawFirmRequired', {
                defaultValue: 'Affiliated law firm is required.',
              }),
            }}
            render={({ field }) => (
              <TextField
                label={t('pbl.lawyerRegistration.profileCv.fields.affiliatedLawFirmZh', {
                  defaultValue: 'Affiliated Law Firm (Chinese)',
                })}
                variant="standard"
                value={field.value || ''}
                onChange={field.onChange}
                fullWidth
                disabled
              />
            )}
          />
        </InputWrapper>
      </MultiInputWrapper>

      <MultiInputWrapper isMobile={isMobile} marginBottom={26}>
        <InputWrapper>
          <Controller
            control={control}
            name="cv_position_en"
            rules={{
              required: t('pbl.lawyerRegistration.profileCv.errors.positionRequired', {
                defaultValue: 'Position is required.',
              }),
            }}
            render={({ field }) => (
              <TextField
                label={t('pbl.lawyerRegistration.profileCv.fields.position', { defaultValue: 'Position' })}
                variant="standard"
                value={field.value || ''}
                onChange={field.onChange}
                fullWidth
                disabled
              />
            )}
          />
        </InputWrapper>
        <InputWrapper>
          <Controller
            control={control}
            name="cv_position_zh"
            rules={{
              required: t('pbl.lawyerRegistration.profileCv.errors.positionRequired', {
                defaultValue: 'Position is required.',
              }),
            }}
            render={({ field }) => (
              <TextField
                label={t('pbl.lawyerRegistration.profileCv.fields.positionZh', {
                  defaultValue: 'Position (Chinese) *',
                })}
                variant="standard"
                value={field.value || ''}
                onChange={field.onChange}
                fullWidth
                disabled
              />
            )}
          />
        </InputWrapper>
      </MultiInputWrapper>

      <MultiInputWrapper isMobile={isMobile} marginBottom={26}>
        <InputWrapper>
          <Controller
            control={control}
            name="cv_admission_year_en"
            rules={{
              required: t('pbl.lawyerRegistration.profileCv.errors.admissionYearRequired', {
                defaultValue: 'Admission year is required.',
              }),
            }}
            render={({ field }) => (
              <TextField
                label={t('pbl.lawyerRegistration.profileCv.fields.admissionYear', {
                  defaultValue: 'Year of Admission as Hong Kong Solicitor *',
                })}
                variant="standard"
                value={field.value || ''}
                onChange={field.onChange}
                fullWidth
                disabled
              />
            )}
          />
        </InputWrapper>
        <InputWrapper>
          <Controller
            control={control}
            name="cv_admission_year_zh"
            rules={{
              required: t('pbl.lawyerRegistration.profileCv.errors.admissionYearRequired', {
                defaultValue: 'Admission year is required.',
              }),
            }}
            render={({ field }) => (
              <TextField
                label={t('pbl.lawyerRegistration.profileCv.fields.admissionYearZh', {
                  defaultValue: 'Year of Admission as Hong Kong Solicitor (Chinese) *',
                })}
                variant="standard"
                value={field.value || ''}
                onChange={field.onChange}
                fullWidth
                disabled
              />
            )}
          />
        </InputWrapper>
      </MultiInputWrapper>

      <MultiInputWrapper isMobile={isMobile} marginBottom={30}>
        <InputWrapper>
          <Controller
            control={control}
            name="cv_other_qualifications_en"
            rules={{
              required: t('pbl.lawyerRegistration.profileCv.errors.otherQualificationsRequired', {
                defaultValue: 'Other qualifications are required.',
              }),
            }}
            render={({ field }) => (
              <TextField
                label={t('pbl.lawyerRegistration.profileCv.fields.otherQualifications', {
                  defaultValue: 'Other Qualifications / Certifications *',
                })}
                variant="standard"
                value={field.value || ''}
                onChange={field.onChange}
                fullWidth
                disabled
              />
            )}
          />
        </InputWrapper>
        <InputWrapper>
          <Controller
            control={control}
            name="cv_other_qualifications_zh"
            rules={{
              required: t('pbl.lawyerRegistration.profileCv.errors.otherQualificationsRequired', {
                defaultValue: 'Other qualifications are required.',
              }),
            }}
            render={({ field }) => (
              <TextField
                label={t('pbl.lawyerRegistration.profileCv.fields.otherQualificationsZh', {
                  defaultValue: 'Other Qualifications / Certifications (Chinese) *',
                })}
                variant="standard"
                value={field.value || ''}
                onChange={field.onChange}
                fullWidth
                disabled
              />
            )}
          />
        </InputWrapper>
      </MultiInputWrapper>

      <MultiInputWrapper isMobile={isMobile} marginBottom={26}>
        <InputWrapper>
          <Controller
            control={control}
            name="cv_languages_en"
            rules={{
              required: t('pbl.lawyerRegistration.profileCv.errors.languageRequired', {
                defaultValue: 'Language is required.',
              }),
            }}
            render={({ field }) => (
              <TextField
                label={t('pbl.lawyerRegistration.profileCv.fields.language', { defaultValue: 'Language *' })}
                variant="standard"
                value={field.value || ''}
                onChange={field.onChange}
                fullWidth
                disabled
              />
            )}
          />
        </InputWrapper>
        <InputWrapper>
          <Controller
            control={control}
            name="cv_languages_zh"
            rules={{
              required: t('pbl.lawyerRegistration.profileCv.errors.languageRequired', {
                defaultValue: 'Language is required.',
              }),
            }}
            render={({ field }) => (
              <TextField
                label={t('pbl.lawyerRegistration.profileCv.fields.languageZh', {
                  defaultValue: 'Language (Chinese) *',
                })}
                variant="standard"
                value={field.value || ''}
                onChange={field.onChange}
                fullWidth
                disabled
              />
            )}
          />
        </InputWrapper>
      </MultiInputWrapper>

      <InputWrapper marginBottom={26}>
        <Controller
          control={control}
          name="cv_primary_practice_areas_en"
          rules={{
            required: t('pbl.lawyerRegistration.profileCv.errors.primaryPracticeAreasRequired', {
              defaultValue: 'Primary practice areas are required.',
            }),
          }}
          render={({ field, fieldState }) => (
            <TextField
              label={t('pbl.lawyerRegistration.profileCv.fields.primaryPracticeAreas', {
                defaultValue: 'Primary Practice Areas *',
              })}
              variant="standard"
              multiline
              minRows={3}
              value={field.value || ''}
              onChange={field.onChange}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              fullWidth
              disabled
            />
          )}
        />
      </InputWrapper>

      <InputWrapper marginBottom={30}>
        <Controller
          control={control}
          name="cv_primary_practice_areas_zh"
          rules={{
            required: t('pbl.lawyerRegistration.profileCv.errors.primaryPracticeAreasRequired', {
              defaultValue: 'Primary practice areas are required.',
            }),
          }}
          render={({ field, fieldState }) => (
            <TextField
              label={t('pbl.lawyerRegistration.profileCv.fields.primaryPracticeAreasZh', {
                defaultValue: 'Primary Practice Areas (Chinese) *',
              })}
              variant="standard"
              multiline
              minRows={3}
              value={field.value || ''}
              onChange={field.onChange}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              fullWidth
              disabled
            />
          )}
        />
      </InputWrapper>

      <InputWrapper marginBottom={30}>
        <Controller
          control={control}
          name="cv_professional_experience_en"
          render={({ field, fieldState }) => (
            <TextField
              label={t('pbl.lawyerRegistration.profileCv.fields.professionalExperience', {
                defaultValue:
                  'Professional Experience Highlights - Brief description of core experience in the above practice areas (If Applicable)',
              })}
              variant="standard"
              multiline
              minRows={3}
              value={field.value || ''}
              placeholder={t('pbl.lawyerRegistration.profileCv.placeholders.professionalExperience', {
                defaultValue:
                  'focus on case handling, service capabilities; no specific client information; within 500 words',
              })}
              onChange={field.onChange}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              fullWidth
            />
          )}
        />
        <Flex alignItems="center" gridGap={12} marginTop="12px">
          <Button
            type="button"
            size="md"
            variant="primary-invert"
            rounded={false}
            onClick={translateProfessionalExperienceEnToZh}
            disabled={isTranslatingEnToZh}
          >
            {isTranslatingEnToZh
              ? t('pbl.lawyerRegistration.profileCv.actions.translating', { defaultValue: 'Translating...' })
              : t('pbl.lawyerRegistration.profileCv.actions.translateToChinese', {
                  defaultValue: 'Translate to Chinese',
                })}
          </Button>
          {translationErrorEnToZh && <ValidationText>{translationErrorEnToZh}</ValidationText>}
        </Flex>
      </InputWrapper>

      <InputWrapper marginBottom={30}>
        <Controller
          control={control}
          name="cv_professional_experience_zh"
          render={({ field, fieldState }) => (
            <TextField
              label={t('pbl.lawyerRegistration.profileCv.fields.professionalExperienceZh', {
                defaultValue: 'Professional Experience Highlights (Chinese)',
              })}
              variant="standard"
              multiline
              minRows={3}
              value={field.value || ''}
              placeholder={t('pbl.lawyerRegistration.profileCv.placeholders.professionalExperienceZh', {
                defaultValue:
                  'Briefly describe core experience in the above practice areas, focusing on case types handled and service capabilities; no specific client information; within 500 words',
              })}
              onChange={field.onChange}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              fullWidth
            />
          )}
        />
        <Flex alignItems="center" gridGap={12} marginTop="12px">
          <Button
            type="button"
            size="md"
            variant="primary-invert"
            rounded={false}
            onClick={translateProfessionalExperienceZhToEn}
            disabled={isTranslatingZhToEn}
          >
            {isTranslatingZhToEn
              ? t('pbl.lawyerRegistration.profileCv.actions.translating', { defaultValue: 'Translating...' })
              : t('pbl.lawyerRegistration.profileCv.actions.translateToEnglish', {
                  defaultValue: 'Translate to English',
                })}
          </Button>
          {translationErrorZhToEn && <ValidationText>{translationErrorZhToEn}</ValidationText>}
        </Flex>
      </InputWrapper>

      <InputWrapper marginBottom={30}>
        <Controller
          control={control}
          name="cv_pro_bono_experience_en"
          render={({ field, fieldState }) => (
            <TextField
              label={t('pbl.lawyerRegistration.profileCv.fields.proBonoExperience', {
                defaultValue:
                  'Pro Bono Service Experience if applicate - description of past pro bono / community legal service experience',
              })}
              variant="standard"
              multiline
              minRows={3}
              value={field.value || ''}
              placeholder={t('pbl.lawyerRegistration.profileCv.placeholders.proBonoExperience', {
                defaultValue: 'including service organization, scope, duration; within 500 words',
              })}
              onChange={field.onChange}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              fullWidth
            />
          )}
        />
      </InputWrapper>

      <InputWrapper marginBottom={40}>
        <Controller
          control={control}
          name="cv_pro_bono_experience_zh"
          render={({ field, fieldState }) => (
            <TextField
              label={t('pbl.lawyerRegistration.profileCv.fields.proBonoExperienceZh', {
                defaultValue: 'Pro Bono Service Experience (Chinese)',
              })}
              variant="standard"
              multiline
              minRows={3}
              value={field.value || ''}
              placeholder={t('pbl.lawyerRegistration.profileCv.placeholders.proBonoExperienceZh', {
                defaultValue:
                  'Briefly describe past pro bono or community legal service experience, including service organization, scope, and duration; within 500 words',
              })}
              onChange={field.onChange}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              fullWidth
            />
          )}
        />
      </InputWrapper>
    </FormPanel>
  );
};

export default ProfileCvStep;
